import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SignaturePad from '../components/SignaturePad';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { translations, languageFlags, languageNames } from '../lib/translations';
import { ArrowLeft, Link as LinkIcon, Download, PencilSimple, Check, FilePdf, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContractViewPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [savedSignature, setSavedSignature] = useState(null);
  const sigPadRef = useRef(null);

  const lang = user?.preferred_language || localStorage.getItem('selectedLanguage') || 'ITA';
  const t = translations[lang];

  useEffect(() => {
    fetchContract();
    fetchSavedSignature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/contracts/${id}`, { withCredentials: true });
      setContract(response.data);
    } catch (err) {
      toast.error('Errore nel caricamento del contratto');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedSignature = async () => {
    try {
      const r = await axios.get(`${API}/owner/profile`, { withCredentials: true });
      if (r.data?.signature) setSavedSignature(r.data.signature);
    } catch {}
  };

  const downloadPdf = async () => {
    try {
      const response = await axios.get(`${API}/contracts/${id}/pdf`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contratto_${id.slice(-8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF scaricato');
    } catch (err) {
      toast.error('Errore nel download del PDF');
    }
  };

  const copyShareLink = async () => {
    const link = `${window.location.origin}/guest/${contract.share_token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t.linkCopied);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = link;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(t.linkCopied);
      } catch (e) {
        toast.error('Copia manualmente: ' + link);
      }
      document.body.removeChild(textArea);
    }
  };

  const saveOwnerSignature = async (sigOverride = null, persistToProfile = false) => {
    let signature = sigOverride;
    if (!signature) {
      if (sigPadRef.current?.isEmpty()) {
        toast.error('Firma richiesta');
        return;
      }
      signature = sigPadRef.current.toDataURL();
    }
    try {
      await axios.post(`${API}/contracts/${id}/sign-owner`, {
        signature,
        user_agent: navigator.userAgent
      }, { withCredentials: true });
      setContract(prev => ({ ...prev, owner_signature: signature }));
      setShowSignaturePad(false);
      toast.success('Firma salvata');
      // Optionally persist signature in owner profile for future contracts
      if (persistToProfile && !savedSignature) {
        try {
          await axios.post(`${API}/owner/signature`, { signature }, { withCredentials: true });
          setSavedSignature(signature);
          toast.success('Firma salvata anche per i prossimi contratti');
        } catch (e) {
          // Non-blocking: contract is signed even if profile save fails
        }
      }
    } catch (err) {
      toast.error('Errore nel salvare la firma');
    }
  };

  const useSavedSignature = async () => {
    if (!savedSignature) return;
    await saveOwnerSignature(savedSignature, false);
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <p className="text-zinc-500">Caricamento...</p>
      </div>
    );
  }

  if (!contract) return null;

  const ct = translations[contract.language];

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-zinc-100 transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft size={24} weight="bold" className="text-zinc-700" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-black text-zinc-900 tracking-tight">{t.contractDetails}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className={`w-2 h-2 rounded-full ${contract.status === 'completed' ? 'bg-green-600' : 'bg-zinc-300'}`} />
                <span className="text-sm text-zinc-500">
                  {contract.status === 'completed' ? t.completed : t.pending}
                </span>
                <span className="text-zinc-300">|</span>
                <span className="text-lg">{languageFlags[contract.language]}</span>
                <span className="text-sm text-zinc-500">{languageNames[contract.language]}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-12 sm:ml-0">
            <Button
              variant="outline"
              onClick={downloadPdf}
              className="rounded-none border-zinc-300 text-xs sm:text-sm"
              data-testid="download-pdf-button"
            >
              <FilePdf size={18} className="mr-1 sm:mr-2" />
              PDF
            </Button>
            {contract.owner_signature ? (
              <Button
                variant="outline"
                onClick={copyShareLink}
                className="rounded-none border-zinc-300 text-xs sm:text-sm"
                data-testid="share-link-button"
              >
                <LinkIcon size={18} className="mr-1 sm:mr-2" />
                {t.share}
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled
                className="rounded-none border-zinc-300 text-xs sm:text-sm opacity-40 cursor-not-allowed"
                data-testid="share-link-button-disabled"
              >
                <LinkIcon size={18} className="mr-1 sm:mr-2" />
                {t.share}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Contract Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-white border border-zinc-200 p-4 sm:p-8 space-y-6 sm:space-y-8">
          {/* Title */}
          <div className="text-center border-b border-zinc-200 pb-6">
            <h2 className="text-xl font-black text-zinc-900 tracking-tight uppercase">
              {contract.language === 'ITA' && 'CONTRATTO DI LOCAZIONE AD USO TURISTICO'}
              {contract.language === 'ENG' && 'TOURIST LEASE AGREEMENT'}
              {contract.language === 'FRA' && 'CONTRAT DE LOCATION TOURISTIQUE'}
              {contract.language === 'SPA' && 'CONTRATO DE ARRENDAMIENTO TURISTICO'}
              {contract.language === 'TED' && 'TOURISTISCHER MIETVERTRAG'}
            </h2>
          </div>

          {/* Section 1: Parties */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">1. {ct.landlord}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-zinc-500">{ct.fullName}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.full_name}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.birthPlace}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.birth_place}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.birthDate}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.birth_date}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.residence}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.residence}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.taxCode}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.tax_code}</p>
              </div>
            </div>
          </div>

          {/* Section: Guest */}
          <div className="border-t border-zinc-200 pt-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">{ct.tenant}</h3>
            {contract.guest_data ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500">{ct.fullName}:</span>
                  <p className="font-medium text-zinc-900">{contract.guest_data.full_name}</p>
                </div>
                <div>
                  <span className="text-zinc-500">{ct.birthPlace}:</span>
                  <p className="font-medium text-zinc-900">{contract.guest_data.birth_place}</p>
                </div>
                <div>
                  <span className="text-zinc-500">{ct.birthDate}:</span>
                  <p className="font-medium text-zinc-900">{contract.guest_data.birth_date}</p>
                </div>
                <div>
                  <span className="text-zinc-500">{ct.residence}:</span>
                  <p className="font-medium text-zinc-900">{contract.guest_data.residence}</p>
                </div>
                <div>
                  <span className="text-zinc-500">{ct.documentNumber}:</span>
                  <p className="font-medium text-zinc-900">{contract.guest_data.document_type_number}</p>
                </div>
                {contract.guest_data.tax_code && (
                  <div>
                    <span className="text-zinc-500">{ct.taxCode}:</span>
                    <p className="font-medium text-zinc-900">{contract.guest_data.tax_code}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-zinc-400 italic">Da compilare dall'ospite</p>
            )}
          </div>

          {/* Section 2: Property */}
          <div className="border-t border-zinc-200 pt-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">2. {ct.propertyObject}</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-zinc-500">{ct.propertyCity}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.property_city}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.propertyProvince}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.property_province}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.propertyAddress}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.property_address}, {contract.owner_data.property_number}</p>
              </div>
              <div>
                <span className="text-zinc-500">Catasto:</span>
                <p className="font-medium text-zinc-900">
                  Foglio {contract.owner_data.cadastral_sheet}, 
                  Particella {contract.owner_data.cadastral_parcel}, 
                  Sub {contract.owner_data.cadastral_sub}
                </p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.cinCode}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.cin_code}</p>
              </div>
            </div>
          </div>

          {/* Section 3: Duration */}
          <div className="border-t border-zinc-200 pt-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">3. {ct.stayDuration}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-zinc-500">{ct.nights}:</span>
                <p className="font-medium text-zinc-900">{contract.rental_data.nights}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.checkIn}:</span>
                <p className="font-medium text-zinc-900">{contract.rental_data.check_in_date} {ct.checkIn === 'Check-in' ? 'at' : 'ore'} {contract.rental_data.check_in_time}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.checkOut}:</span>
                <p className="font-medium text-zinc-900">{contract.rental_data.check_out_date} {ct.checkIn === 'Check-in' ? 'at' : 'ore'} {contract.rental_data.check_out_time}</p>
              </div>
            </div>
          </div>

          {/* Section 4: Payment */}
          <div className="border-t border-zinc-200 pt-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">4. {ct.rentPayment}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-zinc-500">{ct.totalRent}:</span>
                <p className="font-medium text-zinc-900">€{contract.rental_data.total_rent}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.deposit}:</span>
                <p className="font-medium text-zinc-900">€{contract.rental_data.deposit_amount}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.balance}:</span>
                <p className="font-medium text-zinc-900">€{contract.rental_data.balance_amount}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.touristTax}:</span>
                <p className="font-medium text-zinc-900">€{contract.rental_data.tourist_tax}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.paymentMethod}:</span>
                <p className="font-medium text-zinc-900">{contract.rental_data.payment_method}</p>
              </div>
              {contract.rental_data.security_deposit_required && (
                <div>
                  <span className="text-zinc-500">{ct.securityDeposit}:</span>
                  <p className="font-medium text-zinc-900">€{contract.rental_data.security_deposit_amount}</p>
                </div>
              )}
            </div>
          </div>

          {/* Section: House Rules */}
          <div className="border-t border-zinc-200 pt-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">5. {ct.houseRules}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-zinc-500">{ct.maxOccupants}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.max_occupants}</p>
              </div>
              <div>
                <span className="text-zinc-500">{ct.petsAllowed}:</span>
                <p className="font-medium text-zinc-900">{contract.owner_data.pets_allowed ? 'Si' : 'No'}</p>
              </div>
            </div>
          </div>

          {/* Place and Date */}
          <div className="border-t border-zinc-200 pt-6">
            <p className="text-sm text-zinc-500">{ct.placeDate}:</p>
            <p className="font-medium text-zinc-900">{contract.rental_data.place_date}</p>
          </div>

          {/* Signatures */}
          <div className="border-t border-zinc-200 pt-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {/* Owner Signature */}
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">{ct.landlordSignature}</h4>
                {contract.owner_signature ? (
                  <div className="border border-zinc-200 bg-zinc-50 p-4">
                    <img src={contract.owner_signature} alt="Firma proprietario" className="max-h-24" />
                  </div>
                ) : showSignaturePad ? (
                  <div className="space-y-4">
                    {savedSignature && (
                      <div className="bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <img src={savedSignature} alt="Firma salvata" className="h-10 bg-white border border-emerald-100 px-2" />
                          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">Firma precompilata</p>
                        </div>
                        <Button
                          onClick={useSavedSignature}
                          className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider h-8 px-3"
                          data-testid="use-saved-signature-button"
                        >
                          <Check size={13} className="mr-1" /> Usa firma salvata
                        </Button>
                      </div>
                    )}
                    <div className="border border-zinc-300 bg-zinc-50">
                      <SignaturePad
                        ref={sigPadRef}
                        height={200}
                        data-testid="owner-signature-pad"
                      />
                    </div>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={clearSignature}
                          className="rounded-none border-zinc-300 text-sm"
                          data-testid="clear-signature-button"
                        >
                          {ct.clearSignature}
                        </Button>
                        <Button
                          onClick={() => saveOwnerSignature(null, !savedSignature)}
                          className="rounded-none bg-zinc-900 text-white text-sm"
                          data-testid="save-signature-button"
                        >
                          <Check size={16} className="mr-1" />
                          {ct.save}
                        </Button>
                      </div>
                      {!savedSignature && (
                        <div className="bg-amber-50 border-l-4 border-amber-400 px-3 py-2 text-xs text-amber-900 leading-snug max-w-md">
                          ℹ️ <strong>Suggerimento:</strong> La firma sarà salvata per i prossimi contratti — non dovrai più ridisegnarla.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => setShowSignaturePad(true)}
                    className="rounded-none border-zinc-300"
                    data-testid="add-owner-signature-button"
                  >
                    <PencilSimple size={18} className="mr-2" />
                    Aggiungi Firma
                  </Button>
                )}
              </div>

              {/* Guest Signature */}
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-500 mb-4">{ct.tenantSignature}</h4>
                {contract.guest_signature ? (
                  <div className="border border-zinc-200 bg-zinc-50 p-4">
                    <img src={contract.guest_signature} alt="Firma ospite" className="max-h-24" />
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <Check size={14} weight="bold" />
                      Firmato dall'ospite
                    </p>
                  </div>
                ) : (
                  <div className="border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center">
                    <p className="text-zinc-400 text-sm">In attesa della firma dell'ospite</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Download Warning for signed contracts */}
        {contract.status === 'completed' && (
          <div className="mt-6 bg-teal-50 border border-teal-200 p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-teal-900 text-sm">Scarica il PDF per i tuoi archivi</h3>
              <p className="text-teal-700 text-xs mt-1">Il contratto firmato resterà disponibile fino alla fine del mese solare, poi verrà rimosso dal server.</p>
            </div>
            <Button
              onClick={downloadPdf}
              className="rounded-none bg-teal-700 hover:bg-teal-800 text-white font-bold text-sm flex-shrink-0"
              data-testid="urgent-download-pdf-button"
            >
              <FilePdf size={18} className="mr-2" />
              Scarica PDF
            </Button>
          </div>
        )}

        {/* Warning: Sign before sharing */}
        {!contract.owner_signature && contract.status !== 'completed' && (
          <div className="mt-6 bg-red-50 border-2 border-red-300 p-4 sm:p-5 flex items-start gap-3" data-testid="sign-before-sharing-warning">
            <Warning size={24} weight="fill" className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-red-900 text-sm">Firma il contratto prima di inviarlo all'ospite!</h3>
              <p className="text-red-700 text-xs mt-1">Per garantire la validità legale del contratto, apponi la tua firma nella sezione sopra prima di condividere il link con l'ospite.</p>
            </div>
          </div>
        )}

        {/* Share Link Banner */}
        {contract.owner_signature ? (
          <div className="mt-6 bg-zinc-900 text-white p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold">Link per l'ospite</h3>
              <p className="text-zinc-400 text-sm mt-1">Condividi questo link con l'ospite per la compilazione e firma</p>
            </div>
            <Button
              onClick={copyShareLink}
              className="rounded-none bg-white text-zinc-900 hover:bg-zinc-100 font-bold"
              data-testid="copy-guest-link-button"
            >
              <LinkIcon size={18} className="mr-2" />
              Copia Link
            </Button>
          </div>
        ) : (
          <div className="mt-6 bg-zinc-200 p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-bold text-zinc-500">Link per l'ospite</h3>
              <p className="text-zinc-400 text-sm mt-1">Firma il contratto per abilitare il link di condivisione</p>
            </div>
            <Button
              disabled
              className="rounded-none bg-zinc-400 text-white font-bold cursor-not-allowed opacity-60"
              data-testid="copy-guest-link-button-disabled"
            >
              <LinkIcon size={18} className="mr-2" />
              Copia Link
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
